// Browser ESM implementation. Do not require() a .js/.cjs helper from this file:
// CRA/webpack treats that as an ES module and then throws on module.exports.
// Node/build scripts use seoPolicyCore.js (CommonJS) instead — keep behavior in sync.

export const DEFAULT_ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

export interface SeoOverride {
  metaTitle?: string;
  metaDescription?: string;
  canonical?: string;
  keywords?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  jsonLd?: string;
}

export interface SeoInput {
  title: string;
  description?: string;
  canonicalUrl?: string;
  keywords?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  jsonLd?: string;
  forceNoindex?: boolean;
}

function formatTitle(title = '') {
  const clean = title.replace(/\s*\|\s*Dynamic\s+Dental(\s+Clinic)?\s*$/i, '');
  return clean.length + 17 <= 60 ? `${clean} | Dynamic Dental` : clean;
}

export function normalizeUrl(url?: string) {
  if (!url || url === '/') return url;
  try {
    if (/^https?:\/\//i.test(url)) {
      const parsed = new URL(url);
      const last = parsed.pathname.split('/').filter(Boolean).pop() || '';
      if (last.includes('.')) return url;
      if (!parsed.pathname.endsWith('/')) {
        parsed.pathname = `${parsed.pathname}/`;
      }
      return parsed.toString();
    }
  } catch {
    /* Relative paths fall through. */
  }
  return url.endsWith('/') ? url : `${url}/`;
}

export function resolveSeo(base: SeoInput, override: SeoOverride = {}): SeoInput {
  const title = formatTitle(override.metaTitle || base.title || 'Dynamic Dental Clinic');
  const description = override.metaDescription || base.description || '';
  const canonicalUrl = normalizeUrl(override.canonical || base.canonicalUrl);
  return {
    ...base,
    title,
    description,
    canonicalUrl,
    keywords: override.keywords || base.keywords || '',
    robots: base.forceNoindex
      ? 'noindex, nofollow'
      : override.robots || base.robots || DEFAULT_ROBOTS,
    ogTitle: override.ogTitle || override.metaTitle || base.ogTitle || title,
    ogDescription:
      override.ogDescription || override.metaDescription || base.ogDescription || description,
    ogImage: override.ogImage || base.ogImage,
    ogUrl: normalizeUrl(override.canonical || base.ogUrl || canonicalUrl),
    jsonLd: override.jsonLd || base.jsonLd,
  };
}

export function escapeHtml(value: unknown = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function patchSeoHtml(html: string, base: SeoInput, override: SeoOverride = {}) {
  const meta = resolveSeo(base, override);
  const setTag = (pattern: RegExp, tag: string) => {
    html = html.replace(pattern, '');
    html = html.replace(/<\/head>/i, `${tag}\n</head>`);
  };
  setTag(/<title\b[^>]*>[\s\S]*?<\/title>/gi, `<title>${escapeHtml(meta.title)}</title>`);
  const tags: Record<string, string | undefined> = {
    description: meta.description,
    keywords: meta.keywords,
    robots: meta.robots,
    googlebot: meta.robots,
    'twitter:card': 'summary_large_image',
    'twitter:title': meta.ogTitle,
    'twitter:description': meta.ogDescription,
    'twitter:image': meta.ogImage,
    'og:title': meta.ogTitle,
    'og:description': meta.ogDescription,
    'og:image': meta.ogImage,
    'og:url': meta.ogUrl,
    'og:type': meta.ogType || 'website',
  };
  for (const [name, value] of Object.entries(tags)) {
    const pattern = new RegExp(
      `<meta\\b(?=[^>]*(?:name|property)=["']${name}["'])[^>]*>`,
      'gi'
    );
    setTag(
      pattern,
      value
        ? `<meta ${name.startsWith('og:') ? 'property' : 'name'}="${name}" content="${escapeHtml(value)}"/>`
        : ''
    );
  }
  setTag(
    /<link\b(?=[^>]*rel=["']canonical["'])[^>]*>/gi,
    meta.canonicalUrl && !meta.forceNoindex
      ? `<link rel="canonical" href="${escapeHtml(meta.canonicalUrl)}"/>`
      : ''
  );
  if (meta.jsonLd) {
    try {
      const json = JSON.stringify(JSON.parse(meta.jsonLd)).replace(/</g, '\\u003c');
      html = html.replace(
        /<script\b[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi,
        ''
      );
      html = html.replace(
        /<\/head>/i,
        `<script type="application/ld+json" data-custom-seo-jsonld="true">${json}</script>\n</head>`
      );
    } catch {
      /* Invalid CMS JSON must not corrupt the page. */
    }
  }
  return html;
}

export function cleanSeoTemplate(html: string) {
  return html
    .replace(/<main\b[^>]*data-seo-fallback=["']true["'][^>]*>[\s\S]*?<\/main>/gi, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, (script) =>
      script.includes('function removeSeoFallbacks()') ? '' : script
    );
}

export function buildRobots(siteUrl: string) {
  return `User-agent: *\nAllow: /\nDisallow: /admin/\nDisallow: /login\nDisallow: /unsubscribe/\n\nSitemap: ${siteUrl.replace(/\/+$/, '')}/sitemap.xml\n`;
}
